import React, { useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { FaCalendarAlt, FaClock, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import {
  ALL_BLOG_POSTS,
  BLOG_POSTS,
  findPublishedBlogPost,
} from './BlogPage';
import {
  markContentDestination,
  recordArticleView,
} from '../utils/firstPartyAnalytics';
import {
  PAY_TRANSPARENCY_REPORT,
  formatReportRate,
} from '../utils/payTransparencyReport';

/* ── Blog Post Content ────────────────────────────────────────────── */

interface Section {
  heading: string;
  content: RichParagraph[];
  table?: {
    caption: string;
    headers: string[];
    rows: string[][];
    note?: string;
  };
  relatedLinks?: Array<{ href: string; label: string }>;
}

interface PostContent {
  intro: RichParagraph[];
  sections: Section[];
  conclusion: RichParagraph[];
}

interface ProductClaimParagraph {
  html: string;
  productClaimIds: string[];
}

type RichParagraph = string | ProductClaimParagraph;

export const productClaim = (
  claimIdOrIds: string | string[],
  html: string,
): ProductClaimParagraph => ({
  html,
  productClaimIds: Array.isArray(claimIdOrIds) ? claimIdOrIds : [claimIdOrIds],
});

const renderRichParagraph = (para: RichParagraph, className: string, key: React.Key) => (
  <p
    key={key}
    className={className}
    dangerouslySetInnerHTML={{ __html: typeof para === 'string' ? para : para.html }}
  />
);

const POST_CONTENT: Record<string, PostContent> = {
  'stagecrew-pay-transparency-report-2026': {
    intro: [
      'Pay transparency is useful only when the numbers are tied to a defined sample. This report freezes one reproducible snapshot of current pay-disclosed live-event listings instead of presenting advertised rates as a market-wide wage estimate.',
      'The snapshot was captured on August 19, 2026 at 22:15:20 UTC. It contains 137 listings that passed the public qualification boundary at that moment: a supported employer-controlled source, current open-state evidence, an explicit structured pay amount and cadence, a direct application URL, and canonical duplicate suppression.',
    ],
    sections: [
      {
        heading: 'What the snapshot contains',
        content: [
          'All 137 listings in this snapshot disclosed hourly pay. There were no qualified daily, flat-total, monthly, weekly, or salary listings in the sample, so this report does not convert or compare pay across cadences.',
          'Source-family counts describe where the employer-controlled listing data originated. They do not imply a partnership, endorsement, or complete view of any employer\'s hiring.',
        ],
        table: {
          caption: 'Qualified listings by employer-board source family',
          headers: ['Source family', 'Listings'],
          rows: PAY_TRANSPARENCY_REPORT.sourceRows.map(({ source, count }) => [
            source === 'encore' ? 'Encore careers' : source === 'workday' ? 'Workday employer boards' : 'Greenhouse employer boards',
            String(count),
          ]),
          note: 'Counts are listings in the fixed snapshot, not employers, workers, applications, or hires.',
        },
      },
      {
        heading: 'Advertised hourly pay by primary role',
        content: [
          'Each listing is counted once under its first stored normalized role. A disclosed floor is the minimum advertised amount; a disclosed ceiling is the maximum when the listing gives a range, or the same single amount otherwise.',
          'Only primary-role groups with at least five listings appear below. The median is the middle disclosed floor in the sample. These are listing statistics, not estimates of what a worker should earn or what an employer will offer.',
        ],
        table: {
          caption: 'Advertised hourly pay in the fixed listing snapshot',
          headers: ['Primary role', 'Listings', 'Lowest floor', 'Median floor', 'Highest ceiling'],
          rows: PAY_TRANSPARENCY_REPORT.roleRows.map(row => [
            row.label,
            String(row.count),
            `${formatReportRate(row.lowestFloor)}/hr`,
            `${formatReportRate(row.medianFloor)}/hr`,
            `${formatReportRate(row.highestCeiling)}/hr`,
          ]),
          note: 'Three role groups met the five-listing publication threshold. Smaller groups are suppressed rather than summarized from thin samples.',
        },
        relatedLinks: [
          { href: '/roles/av-technician', label: 'AV technician role guide' },
          { href: '/roles/stagehand', label: 'Stagehand role guide' },
          { href: '/roles/rigger', label: 'Rigger role guide' },
        ],
      },
      {
        heading: 'Why this report does not publish city pay averages',
        content: [
          'The listings span many city labels, but the market-level samples are too small and uneven for responsible city pay comparisons. Publishing a city average from only a handful of listings could look more precise than the evidence supports.',
          'Market pages are useful for checking current availability, not for treating this fixed snapshot as a local wage survey. Listing volume and source coverage can change after the snapshot date.',
        ],
        relatedLinks: [
          { href: '/gigs/denver', label: 'Current Denver listings' },
          { href: '/gigs/las-vegas', label: 'Current Las Vegas listings' },
          { href: '/gigs/orlando', label: 'Current Orlando listings' },
          { href: '/gigs/dallas', label: 'Current Dallas listings' },
        ],
      },
      {
        heading: 'How to compare an individual offer',
        content: [
          'Use the advertised rate as one input. Check minimum call length, overtime, worker classification, travel, parking, equipment, union terms, benefits, taxes, schedule, and cancellation terms before comparing two opportunities.',
          'A listing range does not show where an individual offer will fall. Experience, responsibility, venue rules, schedule, and local agreements can all affect the final terms.',
        ],
        relatedLinks: [
          { href: '/blog/av-technician-salary-guide', label: 'AV technician pay guide' },
          { href: '/jobs', label: 'Browse current pay-disclosed jobs' },
        ],
      },
      {
        heading: 'Methods and limitations',
        content: [
          'The underlying artifact stores only listing identifiers, source family, structured minimum and maximum pay, cadence, and normalized roles. It excludes names, contact details, descriptions, application URLs, and other free-form content.',
          'This is an inventory snapshot, not a labor-market survey. Source and regional coverage are uneven; counts represent listings rather than unique employers; advertised pay is not verified take-home pay; and the data does not normalize benefits, overtime, minimum calls, union terms, travel, or actual offers.',
          'A listing being current at the snapshot time does not prove that it remains open later. Review the original employer-controlled application page before relying on any opportunity.',
        ],
      },
    ],
    conclusion: [
      'The clearest finding is narrow: 137 qualified listings disclosed hourly pay, and only three primary-role groups were large enough for a responsible role-level summary. The report deliberately leaves every thinner conclusion unpublished.',
      'Use this snapshot to understand the advertised listing sample, then use current listings, local agreements, and a complete offer to make an individual decision.',
    ],
  },
  'how-to-tell-stagehand-job-listing-is-legit': {
    intro: [
      'A polished logo, recognizable company name, or detailed message does not prove that a job is real. Scammers can copy real brands, impersonate recruiters, and place fake openings on legitimate job boards.',
      'No checklist can guarantee that a listing is legitimate. These nine checks help stagehands and AV technicians slow the process down, verify the opportunity independently, and recognize when it is safer to walk away.',
    ],
    sections: [
      {
        heading: '1. Find the original listing',
        content: [
          'Start with the company, venue, production vendor, or staffing firm named in the post. Look for the opening on its official website or contact the organization through a phone number or email address you found independently—not one supplied only by the recruiter.',
          'The <a href="https://consumer.ftc.gov/consumer-alerts/2023/12/how-spot-latest-job-scams" target="_blank" rel="noopener noreferrer">Federal Trade Commission recommends contacting the company through a website or number you know is legitimate</a> when a recruiter or offer seems uncertain.',
          productClaim(
            'jobs.source-application-path',
            'StageCrew shows the original listing source and available application path for sourced jobs. That context helps you investigate, but StageCrew does not guarantee that an employer, recruiter, or third-party listing is legitimate or still hiring.',
          ),
        ],
      },
      {
        heading: '2. Inspect the recruiter and sender',
        content: [
          'Compare the sender\'s email domain with the organization\'s real domain, including spelling, extra words, and substituted characters. A free email account is not automatically fraudulent—small crews often use them—but it deserves more independent verification when someone claims to represent a large company.',
          'Search the recruiter\'s name and company name with terms such as "scam," "complaint," or "review." The FTC recommends this research while warning that a lack of complaints does not prove an offer is safe.',
          'If the organization has a public staff directory or main office number, use it to confirm that the person works there and is recruiting for the stated role.',
        ],
      },
      {
        heading: '3. Ask for concrete event and job details',
        content: [
          'A real crew call should become more specific as you move toward acceptance. Ask for the venue, event date, call time, estimated end time, department, duties, supervisor, wardrobe or tools, rate, minimum call, overtime terms, payment timing, and worker classification.',
          'Not every legitimate employer can disclose a client or event name immediately. They should still be able to explain the work, hiring process, and who is responsible for paying you.',
          'Vague answers are not proof of fraud, but contradictions about the venue, schedule, role, or payment terms are a reason to pause and verify before traveling or sharing documents.',
        ],
      },
      {
        heading: '4. Never pay someone for the promise of a job',
        content: [
          'Do not send a recruiter money for access, placement, onboarding, equipment release, background checks, training, or a guaranteed crew call. The <a href="https://consumer.ftc.gov/articles/job-scams" target="_blank" rel="noopener noreferrer">FTC says honest employers do not ask candidates to pay to get a job</a>.',
          'Workers sometimes choose to pay legitimate third parties directly for recognized certifications, licenses, union obligations, or their own tools. That is different from sending money to an unknown recruiter or payment account as a condition of receiving a job.',
          'Gift cards, cryptocurrency, wire transfers, and person-to-person payment apps are especially serious warning signs when the recipient claims the payment will unlock work or be reimbursed later.',
        ],
      },
      {
        heading: '5. Stop if a check comes with purchasing instructions',
        content: [
          'A common fake-job script sends a check for equipment, supplies, travel, or an "overpayment," then tells the worker to buy something or return part of the money. A bank may make funds appear available before discovering that the check is fake.',
          'The <a href="https://consumer.ftc.gov/articles/how-spot-avoid-and-report-fake-check-scams" target="_blank" rel="noopener noreferrer">FTC warns that fake checks can take weeks to be identified</a>. If you send money in the meantime, you may be responsible for repaying the bank.',
          'Do not forward money, buy gift cards, transfer cryptocurrency, or pay a supposed vendor with funds from a check sent by someone you do not independently know and trust.',
        ],
      },
      {
        heading: '6. Protect sensitive information until the employer is verified',
        content: [
          'A resume and basic contact information are normal early in hiring. A Social Security number, driver\'s license image, passport, bank information, tax forms, or account credentials deserve a much higher level of caution.',
          'The <a href="https://www.ic3.gov/PSA/2020/PSA200121" target="_blank" rel="noopener noreferrer">FBI\'s Internet Crime Complaint Center warns that fake employers use job listings to collect personally identifiable information</a>. Verify the company and hiring contact before sending sensitive documents, and use the employer\'s established secure onboarding process after a real interview and offer.',
          'Never share passwords, one-time verification codes, or remote access to your device as part of an application.',
        ],
      },
      {
        heading: '7. Slow down urgency and messaging-only hiring',
        content: [
          'Live-event hiring can move quickly, and many legitimate crew leads use text messages. Speed by itself does not mean fraud. The concern is pressure that prevents you from checking the company, asking questions, or reading the terms.',
          'Be cautious when an unsolicited contact offers the job before a meaningful conversation, refuses a phone or video call, moves you immediately to an unfamiliar messaging service, or insists that you act before you can verify the event.',
          'Logos, offer letters, and even lengthy interviews can be copied or staged. Independent confirmation matters more than how polished the message looks.',
        ],
      },
      {
        heading: '8. Check the date, duplicates, and application path',
        content: [
          'Confirm when the listing was posted, whether the application deadline or event date has passed, and whether the original source still accepts applications. An old or duplicated listing is not necessarily a scam, but it may be filled, expired, or copied without authorization.',
          'Use the application method shown by the original source. Treat a last-minute request to send documents to a different address, shortened link, or unrelated domain as a new claim that needs verification.',
          productClaim(
            ['jobs.unavailable-recovery', 'jobs.active-discovery'],
            'On StageCrew, review any date warning and open the original source before relying on an active listing. If StageCrew marks a listing unavailable, return to the active jobs page instead of using the old application path. You can <a href="/jobs">browse active sourced listings</a>, but every worker should still evaluate the original opportunity.',
          ),
        ],
      },
      {
        heading: '9. Know when to stop and report',
        content: [
          'Stop communicating if the contact asks for money, sensitive information before verification, check forwarding, gift cards, cryptocurrency, or account access. Save the listing URL, messages, sender details, and payment records without continuing the transaction.',
          'Report suspected job fraud to the <a href="https://reportfraud.ftc.gov/" target="_blank" rel="noopener noreferrer">FTC at ReportFraud.ftc.gov</a>. Internet-enabled fraud can also be reported to the <a href="https://www.ic3.gov/" target="_blank" rel="noopener noreferrer">FBI Internet Crime Complaint Center</a>. Report the listing to the platform where you found it as well.',
          productClaim(
            'jobs.listing-report-review',
            'If you found the listing on StageCrew, signed-in users can select Report listing on an active job page and choose a structured reason for administrator review. A report does not automatically remove or penalize the listing.',
          ),
          'If you already sent money or financial information, contact the bank, card issuer, payment app, or transfer provider immediately and ask whether the transaction can be stopped or reversed.',
          'If you shared a Social Security number or identity document, use the federal recovery planner at <a href="https://www.identitytheft.gov/" target="_blank" rel="noopener noreferrer">IdentityTheft.gov</a> to choose the next steps for your situation.',
        ],
      },
    ],
    conclusion: [
      'Before applying, verify the source, recruiter, event details, payment terms, and application path. Never pay to get a job, and never use an employer-supplied check to send money elsewhere.',
      productClaim(
        'jobs.active-discovery',
        'StageCrew lists sourced opportunities, but the safest final step is always independent verification with the organization offering the work.',
      ),
    ],
  },
  'how-to-get-more-stagehand-gigs': {
    intro: [
      'Whether you\'re just starting out or you\'ve been loading trucks for years, one thing stays the same: the best stagehands are the ones who never stop hustling for their next call.',
      'Live-event work can be competitive and varies by season and market. Here are practical ways stagehands and AV techs can make their experience easier for employers to evaluate.',
    ],
    sections: [
      {
        heading: '1. Build a Killer Profile',
        content: [
          productClaim(
            'workers.public-profiles',
            'Your profile is your resume in the gig economy. Employers can review the specific skills, certifications, and work history you choose to provide.',
          ),
          productClaim(
            ['workers.profile-preferences', 'workers.profile-matching'],
            'A complete StageCrew profile gives matching tools and employers more relevant information to work with, but it does not guarantee a particular number of matches or bookings.',
          ),
          'Pro tip: List every piece of gear you\'re certified on. "Audio engineer" is vague. "Yamaha CL5 / DiGiCo SD-Series / Dante certified" gets you hired.',
        ],
      },
      {
        heading: '2. Say Yes to the First Call',
        content: [
          'Reputation in this industry is everything. When a crew chief or production manager calls you for the first time, say yes — even if it\'s not your ideal gig.',
          'That corporate AV load-in at the convention center? It might not be a stadium concert, but the crew lead may staff recurring events. Treat every call professionally.',
          'Arrive before the stated call time, follow instructions, and communicate professionally. Those habits support your reputation but do not guarantee another call.',
        ],
      },
      {
        heading: '3. Diversify Your Skill Set',
        content: [
          'Versatile stagehands often cross-train across audio, lighting, video, and rigging.',
          'If you\'re primarily a stagehand doing load-ins/load-outs, learn basic audio patching or lighting focus. If you\'re an audio tech, learn video switching. Each new skill opens entirely new gig categories.',
          'Markets like Las Vegas, Nashville, and Atlanta especially value versatile techs who can handle festival stages, corporate AV, and broadcast simultaneously.',
        ],
      },
      {
        heading: '4. Centralize Your Search',
        content: [
          productClaim(
            'workers.profile-preferences',
            'Instead of manually checking every source, use StageCrew to save your role, location, rate, and availability preferences in one place.',
          ),
          'If you use any application-automation tool, prefer a review-first mode and inspect every result. Submission and employer response are never guaranteed.',
          'Keep your availability and rate preferences current, and review the original listing before relying on its dates, requirements, or application status.',
        ],
      },
      {
        heading: '5. Network at Every Gig',
        content: [
          'Every gig is a networking event. Exchange numbers with other techs, crew chiefs, and production managers. The stagehand who gets the next call is the one they remember.',
          'Join local IATSE chapters and industry Facebook groups for your market. Attend trade shows like LDI, InfoComm, and NAB if you can.',
          'Referrals and repeat contacts remain important alongside job marketplaces, so treat every professional relationship as part of your long-term network.',
        ],
      },
      {
        heading: '6. Pick the Right Markets',
        content: [
          productClaim(
            'markets.role-city-pages',
            'StageCrew publishes city guides for markets such as Las Vegas, Nashville, Los Angeles, New York, Atlanta, and Chicago. These links are not a ranking or promise of current inventory; check current sourced listings.',
          ),
          'Consider travel only after comparing the posted rate with transportation, lodging, schedule, and local working requirements.',
          productClaim(
            'markets.role-city-pages',
            'StageCrew publishes city-specific pages for supported markets. Check the current listing source and application details before making plans.',
          ),
        ],
      },
    ],
    conclusion: [
      'Getting more stagehand gigs isn\'t about luck — it\'s about systems. Build a strong profile, be reliable, expand your skills, review listings consistently, and never stop networking.',
      'Treat your stagehand work like a business: keep your details current, verify each call, and follow up professionally.',
    ],
  },

  'av-technician-salary-guide': {
    intro: [
      'If you\'re considering a career in AV or comparing an offer, this guide explains the role, scope, market, and contract factors that can affect pay. It does not rely on a purported national earnings dataset.',
      'AV tech salaries vary significantly by role, experience, market, and whether you\'re freelance or full-time. Here\'s what you need to know.',
    ],
    sections: [
      {
        heading: 'Average AV Technician Pay by Role',
        content: [
          'This guide does not present a verified marketplace pay-rate dataset. Rates vary by location, contract, role, experience, schedule, union rules, equipment, and travel.',
          'Stagehand / Grip: Compare the physical scope, equipment-operation requirements, minimum call, overtime, and load-in/load-out schedule.',
          'Audio Technician (A2): Console familiarity, RF work, patching responsibility, and show complexity can affect an offered rate.',
          'Audio Engineer (A1): FOH or monitor responsibility, system design, rehearsal time, and production scale can materially change an offer.',
          'Lighting Technician: Programming responsibility, console requirements, focus calls, and equipment complexity matter.',
          'Video Technician: Switching, playback, camera, projection, and LED-wall responsibilities should be priced separately when the scope differs.',
          'Rigger: Confirm required credentials, local rules, insurance, equipment, and safety responsibilities before comparing offers.',
        ],
      },
      {
        heading: 'Freelance vs. Full-Time',
        content: [
          'Freelance offers may use a higher day or hourly rate while leaving benefits, taxes, insurance, downtime, travel, and equipment costs to the worker.',
          'Full-time compensation may include benefits and more predictable hours. Compare the complete compensation package rather than treating a posted rate as equivalent.',
          'The sweet spot for many is a hybrid approach: a part-time base gig at a venue or rental house, supplemented with freelance calls for festivals and one-offs.',
        ],
      },
      {
        heading: 'Pay by Market',
        content: [
          'Location can affect rates, expenses, union requirements, demand, and seasonality. Verify current local information before setting a rate or accepting work.',
          'Las Vegas: Compare convention, residency, arena, and union calls, including overtime and turnaround terms.',
          'New York City: Confirm venue jurisdiction, union requirements, travel time, and the exact scope of a theater or corporate call.',
          'Los Angeles: Studio, broadcast, corporate, and live-event offers can have different classifications and working terms.',
          'Nashville: Music, touring, hotel, and corporate AV calls may price the same role differently.',
          'Atlanta: Film, broadcast, convention, and live-event work can carry different schedules and equipment expectations.',
        ],
      },
      {
        heading: 'How to Increase Your Earning Potential',
        content: [
          'Get certified where it is relevant: credentials such as ETCP or CTS can help employers evaluate role-specific knowledge, but their effect on pay varies.',
          'Learn role-relevant consoles: Some calls specify platforms such as DiGiCo, Avid S6L, grandMA3, or Disguise. Describe your actual operating experience precisely.',
          'Research local union options: Membership, dispatch, benefits, eligibility, and rates differ by local and agreement. Verify current terms directly with the relevant union.',
          productClaim(
            'workers.availability-calendar',
            'Keep your availability current: use StageCrew\'s availability calendar to record weekly availability and blocked dates. Availability does not guarantee calls.',
          ),
        ],
        relatedLinks: [
          { href: '/blog/stagecrew-pay-transparency-report-2026', label: '2026 pay transparency listing report' },
        ],
      },
    ],
    conclusion: [
      'Your earning potential depends on specialization, experience, credentials, market, schedule, and the terms of each engagement. Use current local job postings, union agreements, and employer offers to validate a rate.',
      'Treat every published range as context rather than a promise, and compare the full scope and costs before accepting work.',
    ],
  },

  'event-staffing-guide-for-employers': {
    intro: [
      'Whether you\'re a production company staffing 50 events a year or a corporate planner booking AV for your annual conference, finding reliable event crew is one of the biggest challenges in the industry.',
      'This guide covers everything employers need to know about sourcing, vetting, and booking AV technicians and stagehands efficiently.',
    ],
    sections: [
      {
        heading: 'The Event Staffing Challenge',
        content: [
          'Live-event employers often coordinate across phone calls, group texts, referrals, and job boards, which can make it hard to compare availability and experience in one place.',
          'Traditional staffing methods (phone trees, group texts, Facebook posts) are slow and unreliable. By the time you\'ve called 15 people, the best techs are already committed.',
          productClaim(
            ['workers.profile-preferences', 'workers.profile-matching'],
            'StageCrew compares gig details with worker-provided profiles and saved preferences. Employers still review candidates, verify important qualifications, communicate directly, and make their own hiring decisions.',
          ),
        ],
      },
      {
        heading: 'How to Write a Gig Posting That Attracts Top Talent',
        content: [
          'The best workers have options. Your gig posting needs to stand out. Here\'s what top employers include:',
          'Specific roles needed: Don\'t just say "need crew." Specify: "2x A2 audio techs, 1x lighting programmer (grandMA3), 4x stagehands for load-in." Specificity attracts the right people.',
          'Clear schedule: Load-in time, show time, load-out time, and estimated total hours. Workers plan their week around these details.',
          'Rate or rate range: You don\'t have to publish exact numbers, but indicating "competitive rates" or a range helps serious techs self-select.',
          'Venue and parking info: Especially for convention centers and arenas. Workers need to know where to report and where to park.',
        ],
      },
      {
        heading: 'Vetting AV Technicians',
        content: [
          'Not all techs are created equal. Here\'s what to look for:',
          productClaim(
            'workers.profile-preferences',
            'Skills and certifications: Review worker-provided profile details relevant to the role, and verify any certification or license that matters to your production.',
          ),
          productClaim(
            'workers.public-profiles',
            'Work history, reviews, and references: Signed-in employers can use worker-provided information on an employer-visible profile as a starting point, then ask follow-up questions appropriate to the role.',
          ),
          productClaim(
            ['workers.profile-preferences', 'workers.public-profiles'],
            'Availability and reliability: Confirm the date, call time, travel, and backup plan directly with each candidate. Do not assume any profile field predicts whether a worker will show up.',
          ),
          'Gear ownership: For some roles, worker-owned equipment may be relevant. Confirm compatibility, insurance, rental terms, backups, and who is responsible for loss or damage.',
        ],
      },
      {
        heading: 'Staffing Timelines',
        content: [
          'Lead time depends on the market, role, venue, season, and event complexity; no marketplace can guarantee availability on a particular schedule.',
          'For major events, identify critical positions first and confirm the scope, rate, and reporting details directly.',
          'For corporate AV, publish complete breakout and general-session requirements before finalizing the crew plan.',
          'For last-minute calls, use multiple outreach channels and keep a backup plan. A complete role, rate, date, and location helps workers evaluate the opportunity.',
          'Check the local event calendar and venue requirements before setting a staffing timeline.',
        ],
      },
      {
        heading: 'Reducing No-Shows and Cancellations',
        content: [
          'No-shows can create overtime, schedule, and client problems. Here are practical ways to reduce the risk:',
          'Confirm before the call: Send a direct confirmation with call time, venue, parking, scope, rate, and point of contact. Do not assume a marketplace profile is an automatic crew confirmation.',
          'Build a bench: Don\'t staff exactly to need. Have 1-2 backup techs confirmed as alternates for critical roles.',
          'Pay on time: Workers talk. If you have a reputation for slow payments or rate disputes, the best techs will stop answering your calls.',
          'Review the available profile and references: Ask about directly relevant experience, then document confirmations and keep a backup plan for critical roles.',
        ],
      },
      {
        heading: 'Current Marketplace Boundaries',
        content: [
          productClaim(
            ['employers.gig-posting', 'workers.profile-matching'],
            'StageCrew provides AV and stagehand gig-posting, worker-profile, and matching workflows. Here are the current product boundaries:',
          ),
          productClaim(
            ['workers.profile-matching', 'workers.profile-preferences'],
            'Profile- and preference-based matching: Post complete gig details and review workers whose saved information aligns with the role. A match is not a qualification, availability, or hiring guarantee.',
          ),
          productClaim(
            'workers.public-profiles',
            'Worker-provided profiles: Review skills, certifications, work history, and available reviews, then verify important details directly before hiring.',
          ),
          productClaim(
            'employers.free-posting',
            'First 10 standard gig posts free: eligible employers can start with the disclosed free-post allowance before choosing a subscription.',
          ),
          productClaim(
            'roles.taxonomy-pages',
            'Role-specific pages: StageCrew publishes distinct pages for roles such as A1 audio engineer, A2 audio technician, stagehand, and rigger.',
          ),
        ],
      },
    ],
    conclusion: [
      'Event staffing doesn\'t have to be a scramble. With the right tools and processes, you can build a reliable crew pipeline that scales with your business.',
      'Employers can improve staffing outcomes by documenting the scope, confirming details, and maintaining a reliable hiring process.',
    ],
  },

  'how-to-find-union-stagehands-chicago': {
    intro: [
      'Chicago has an established IATSE presence alongside convention, theater, music, and corporate-event work. Current crew volume varies by venue, season, production, and source.',
      'If you\'re producing an event in Chicago and need union crew, here\'s everything you need to know — from how IATSE jurisdiction works to where to find both union and non-union labor.',
    ],
    sections: [
      {
        heading: 'How IATSE Works in Chicago',
        content: [
          'IATSE (International Alliance of Theatrical Stage Employees) is the primary union representing stagehands, AV technicians, and production workers in live events and film.',
          'In Chicago, the key IATSE locals are Local 2 (stagehands), Local 110 (projectionists), and Local 476 (film/TV). Each local has jurisdiction over specific types of work and venues.',
          'When you book an event at a union venue like McCormick Place, United Center, or the Chicago Theatre, you\'re typically required to use union labor for certain roles. Non-union crew can often supplement union crews, but core positions (riggers, spotlights, audio) are usually union-only.',
        ],
      },
      {
        heading: 'When You Need Union Crew',
        content: [
          'Union crew is required (or strongly preferred) in several scenarios: events at union venues, productions that fall under union jurisdiction agreements, and clients who specify IATSE labor in their contracts.',
          'Major convention centers, arenas, Broadway-touring houses, and TV broadcast venues in Chicago are typically unionized. If you\'re unsure, call the venue before booking — they\'ll tell you exactly what\'s required.',
          'For corporate events at hotels, rooftop venues, or non-union spaces, you generally have more flexibility on labor sourcing.',
        ],
      },
      {
        heading: 'Where to Find Union Stagehands in Chicago',
        content: [
          'The most direct route is contacting IATSE Local 2 directly. They maintain a hiring hall that dispatches stagehands to signatory employers. You\'ll need to register as a signatory employer first.',
          'Labor companies specializing in union labor are another option. These companies handle payroll, benefits, and dispatch, reducing your administrative burden. The Labor Connection is one such provider in Chicago.',
          productClaim(
            'vendors.directory',
            'StageCrew\'s vendor directory includes claimed and unclaimed company listings. Verify union status, services, availability, and contractual terms directly with each provider.',
          ),
        ],
      },
      {
        heading: 'Union vs. Non-Union Rates in Chicago',
        content: [
          'Union rates and working rules vary by local agreement, venue, role, schedule, and production. Request the current applicable rate sheet and collective-bargaining terms before budgeting.',
          'Confirm overtime, meal, turnaround, minimum-call, benefit, and jurisdiction rules in writing with the venue, local, or signatory employer.',
          'Non-union offers also vary by role and contract. Compare the complete scope, insurance, payroll, tax, overtime, and cancellation terms instead of assuming one labor model will cost less.',
        ],
      },
      {
        heading: 'Building a Mixed Labor Strategy',
        content: [
          'Many Chicago productions use a hybrid approach: union crew for required positions at union venues, supplemented by non-union crew for non-jurisdictional roles.',
          'This requires coordination between your union and non-union labor providers to ensure there are no jurisdictional disputes. Work with experienced local labor companies who understand the Chicago landscape.',
          'For multi-day conventions at McCormick Place, it\'s common to have IATSE stagehands for setup and strike, with non-union AV techs running the show floor during the event itself.',
        ],
      },
    ],
    conclusion: [
      productClaim(
        'vendors.directory',
        'For union venues, contact IATSE Local 2 or a signatory labor company. StageCrew\'s directory can help you identify companies to research, but you should verify each provider\'s union status, services, and terms directly.',
      ),
      'The key is knowing your venue\'s requirements before you start booking crew — it saves time, money, and a lot of last-minute scrambling.',
    ],
  },

  'iatse-vs-non-union-crew': {
    intro: [
      'Union or non-union? For event producers in the U.S., this question comes up constantly — and the answer isn\'t always obvious. The right choice depends on your venue, your budget, your client\'s requirements, and the market you\'re in.',
      'Here\'s a clear, practical breakdown of the differences between IATSE union crew and non-union freelance crew, and how to decide which makes sense for your event.',
    ],
    sections: [
      {
        heading: 'What Is IATSE?',
        content: [
          'IATSE (International Alliance of Theatrical Stage Employees) is the labor union representing workers in live theater, film, television, and live events across North America.',
          'IATSE locals represent a range of live-theater, film, television, and event roles. Membership, jurisdiction, and covered work differ by local and agreement.',
          'When a venue or production is "signatory," they\'ve agreed to IATSE\'s collective bargaining agreement (CBA), which sets minimum rates, working conditions, and benefit contributions.',
        ],
      },
      {
        heading: 'The Case for Union Crew',
        content: [
          'Guaranteed minimums: IATSE rates set a floor, which often means more experienced workers. You\'re less likely to get someone who\'s never run a console before.',
          'Built-in benefits and protections: IATSE workers have health insurance and pension contributions, which can reduce turnover and increase reliability on long-running productions.',
          'Required at major venues: Many arenas, convention centers, and Broadway touring venues require union labor for certain roles. There\'s no choice — you need union crew.',
          'Strong in specific markets: New York, Chicago, Las Vegas, Los Angeles, and San Francisco have deep union labor pools. In these cities, top-tier crew is often union.',
        ],
      },
      {
        heading: 'The Case for Non-Union Crew',
        content: [
          'Contract flexibility: Non-union terms are negotiated per engagement, but applicable wage, overtime, insurance, classification, and other legal obligations still need to be verified.',
          'Availability: In markets with smaller IATSE locals, non-union freelancers may outnumber union workers. For corporate events in secondary markets (Phoenix, Denver, Charlotte), non-union is often the practical choice.',
          productClaim(
            ['workers.profile-matching', 'workers.public-profiles'],
            'Marketplace search: StageCrew can surface profile- and preference-based matches and worker profiles for employer review. Response time, availability, qualifications, and hiring are not guaranteed.',
          ),
          'No jurisdiction restrictions: Non-union crew can cover any role you need them to, without worrying about what\'s in-scope for their local.',
        ],
      },
      {
        heading: 'Key Cost Differences',
        content: [
          'The cost difference between union and non-union labor varies by agreement, venue, role, schedule, market, and the services included.',
          'Union agreements may address overtime, meal periods, turnaround, minimum calls, and benefit contributions. Obtain the current agreement rather than relying on a generic percentage.',
          'For non-union work, confirm the rate basis and all applicable payroll, classification, overtime, insurance, expense, and cancellation terms in writing.',
        ],
      },
      {
        heading: 'How to Decide',
        content: [
          'Start with the venue: Is it a union house? If yes, check what roles have jurisdiction. You may not have a choice for certain positions.',
          'Check your client\'s requirements: Some corporate clients or touring productions specify IATSE labor in their rider or contract. Always read the rider before staffing.',
          'Evaluate your market: Union presence and venue jurisdiction vary locally. Confirm the applicable agreement and hiring channel directly.',
          'Consider the event type: Broadway and major concerts almost always require union crew. Corporate conferences, smaller festivals, and private events are typically flexible.',
        ],
      },
    ],
    conclusion: [
      'There\'s no universal right answer between IATSE and non-union crew. The best producers understand both systems and know how to navigate them based on venue, market, and event type.',
      productClaim(
        ['vendors.directory', 'workers.public-profiles'],
        'StageCrew\'s vendor directory can help you identify providers by city, while the marketplace can surface worker profiles for review. Verify union status, qualifications, availability, and terms directly.',
      ),
    ],
  },

  'hire-last-minute-event-crew': {
    intro: [
      'It happens to every event producer eventually. A crew member cancels the morning of the show. Someone doesn\'t show up for load-in. You need an A1 by 2 PM and it\'s already 10 AM.',
      'Here is a practical outreach, confirmation, and backup checklist. No channel or marketplace can guarantee a response or completed hire on a particular timeline.',
    ],
    sections: [
      {
        heading: 'Step 1: Post to a Marketplace Immediately',
        content: [
          productClaim(
            'employers.gig-posting',
            'Post the specific role, call time, location, rate, and application details on StageCrew so workers can evaluate the opportunity. Visibility, responses, and staffing are not guaranteed.',
          ),
          'Be specific: "Need A2 audio tech at [VENUE], [CALL TIME], [ESTIMATED HOURS], [RATE], [SCOPE]" gives workers more useful information than "need audio help."',
          'Post it now, not in an hour. Every minute counts when you\'re racing against a call time.',
        ],
      },
      {
        heading: 'Step 2: Work Your Network in Parallel',
        content: [
          'While your marketplace post is live, text every tech you\'ve worked with in the past year. Keep your messages short and specific: "Are you available today? Need [ROLE] at [VENUE], [TIME] call, [RATE]/day. Reply if yes."',
          'Don\'t leave voicemails — they slow everything down. Texts and DMs get faster responses.',
          'Post in local Facebook groups and Slack channels for AV/stagehand workers in your market. Many markets have active communities that respond quickly to last-minute calls.',
        ],
      },
      {
        heading: 'Step 3: Call a Labor Company',
        content: [
          'If your network isn\'t coming through, call a local labor company or staffing agency. They maintain rosters of available crew and can often dispatch someone within hours.',
          'Ask the labor company for a written quote that separates the worker rate, service fee, payroll, insurance, minimums, cancellation terms, and any other charges.',
          'Have two or three labor companies in your market saved in your contacts before you need them. You don\'t want to be Googling for options when you\'re already in crisis mode.',
        ],
      },
      {
        heading: 'What to Offer for Last-Minute Calls',
        content: [
          'Available crew for last-minute calls know they have leverage. Offering your standard rate may not be enough to pull someone away from their day.',
          'Consider a clearly disclosed rush premium based on your budget and local market: "This is a last-minute call — I\'m offering [RATE], including any rush premium." A premium does not guarantee a response.',
          'Confirm parking, exact check-in location, and who to ask for on arrival. Friction at the venue makes people less likely to say yes to future last-minute calls from you.',
        ],
      },
      {
        heading: 'How to Prevent This Next Time',
        content: [
          'Build a bench: For every key role on a show, have one or two backup techs confirmed as alternates. Send them the call sheet and let them know they\'re on standby. Most won\'t need to be used, but they\'re there when you do.',
          'Confirm in advance: Send the call time, parking, scope, rate, and point of contact, then ask for a direct acknowledgement. Clear communication helps, but it does not guarantee attendance.',
          productClaim(
            'workers.public-profiles',
            'Review worker-provided profiles and references as a starting point, then ask candidates about relevant work and confirm availability directly. Do not treat profile details as a reliability guarantee.',
          ),
          'Follow the agreed payment terms: Put the schedule and invoicing requirements in writing and resolve discrepancies promptly.',
        ],
      },
    ],
    conclusion: [
      'Last-minute crew emergencies are stressful, but they\'re solvable with the right process. Post immediately, work your network in parallel, and be willing to pay a rush premium to pull available crew.',
      'The longer-term fix is building a bench, confirming details directly, and documenting a repeatable outreach process. No marketplace can guarantee a worker response or completed booking.',
    ],
  },
  'top-av-staffing-markets-2026': {
    intro: [
      'Event activity differs by city, season, venue, and production type. This guide identifies markets and work categories to research; it is not a verified ranking of gig volume, pay, demand, or growth.',
      'Before relocating, touring, or setting a hiring budget, review current source listings, local union agreements, written offers, travel costs, and venue requirements.',
    ],
    sections: [
      {
        heading: 'Las Vegas, Nevada',
        content: [
          'Las Vegas hosts conventions, residencies, corporate events, sports, and casino productions at venues such as the Las Vegas Convention Center, Mandalay Bay, and Sphere. Listing volume and requirements change with each event calendar.',
          productClaim(
            'markets.role-city-pages',
            'Search categories commonly include LED, video, rigging, stagehand, and audio work. <a href="/gigs/las-vegas">Browse current Las Vegas listings</a>, then verify the original source, rate, schedule, and application details.',
          ),
        ],
      },
      {
        heading: 'Los Angeles, California',
        content: [
          'Los Angeles work can span corporate events, studios, broadcast, award shows, convention venues, stadiums, and hotel ballrooms. Each segment can use different hiring channels and working terms.',
          productClaim(
            'markets.role-city-pages',
            'Relevant search categories include video, camera, lighting, broadcast, stagehand, and audio roles. <a href="/gigs/los-angeles">Review current Los Angeles listings</a> and compare the stated scope and terms.',
          ),
        ],
      },
      {
        heading: 'New York City & Nashville',
        content: [
          productClaim(
            'markets.role-city-pages',
            'New York City work includes theater, corporate events, fashion, convention, and broadcast production. Venue jurisdiction and IATSE Local 1 agreements may affect a call, so verify them directly. <a href="/gigs/new-york">See current New York listings</a>.',
          ),
          productClaim(
            'markets.role-city-pages',
            'Nashville work can include live music, backline, arena, convention, hotel, and corporate AV calls. <a href="/gigs/nashville">Explore current Nashville listings</a> and confirm the local rate and schedule for each source.',
          ),
        ],
      },
      {
        heading: 'Chicago, Orlando & Atlanta',
        content: [
          productClaim(
            'markets.role-city-pages',
            'Chicago work can include conventions at McCormick Place, festivals, corporate events, theater, and union calls. <a href="/gigs/chicago">Check current Chicago listings</a> and verify venue and union requirements.',
          ),
          productClaim(
            'markets.role-city-pages',
            'Orlando work can include convention, hotel, theme-park, corporate, projection, and LED support. <a href="/gigs/orlando">Browse current Orlando listings</a>.',
          ),
          productClaim(
            'markets.role-city-pages',
            'Atlanta work can include film, television, stadium, convention, corporate, video, and lighting production. <a href="/gigs/atlanta">See current Atlanta listings</a>.',
          ),
        ],
      },
      {
        heading: 'Dallas, Austin & San Francisco',
        content: [
          productClaim(
            'markets.role-city-pages',
            'Dallas work can include convention, stadium, corporate, audio, and general AV calls. <a href="/gigs/dallas">Find current Dallas listings</a>.',
          ),
          productClaim(
            'markets.role-city-pages',
            'Austin work can include festivals, music, sports, technology conferences, experiential events, audio, video, and stagehand calls. <a href="/gigs/austin">Explore current Austin listings</a>.',
          ),
          productClaim(
            'markets.role-city-pages',
            'San Francisco and the Bay Area can include convention, corporate, and technology-event production. <a href="/gigs/san-francisco">See current San Francisco listings</a> and compare travel and local terms.',
          ),
        ],
      },
    ],
    conclusion: [
      'Travel can expand the listings you review, but it also adds transport, lodging, tax, union, and scheduling considerations. Compare the complete terms before committing.',
      productClaim(
        ['jobs.active-discovery', 'accounts.free-registration', 'workers.profile-preferences'],
        '<a href="/jobs">Browse current StageCrew listings</a> or <a href="/register">create a free account</a> to save work preferences.',
      ),
    ],
  },
  'audio-engineer-vs-audio-technician': {
    intro: [
      'If you\'ve worked in live events for more than a day, you\'ve heard the terms "A1" and "A2" thrown around on every job site, load-in sheet, and crew call. But if you\'re newer to the industry — or transitioning from studio work to live sound — the distinction between an audio engineer (A1) and an audio technician (A2) can be confusing.',
      'Understanding the A1/A2 distinction helps you compare responsibilities, scope, and required experience. Verify current rates through local agreements, source listings, and written offers.',
    ],
    sections: [
      {
        heading: 'What an A1 (Audio Engineer) Actually Does',
        content: [
          'The A1 is the lead audio position on any production. They\'re responsible for the overall sound design and mix — the person sitting at front-of-house (FOH) or in the broadcast truck making real-time decisions about what the audience hears. On a corporate show, the A1 mixes keynote presentations, panel discussions, and walk-in music. On a concert, they\'re mixing the band.',
          'Beyond mixing, the A1 typically designs the audio system for the event: selecting speakers, determining placement, specifying microphone choices, and creating the signal flow. When something goes wrong — feedback, a dead mic, a hum in the system — the A1 is expected to diagnose and solve it immediately.',
          productClaim(
            'roles.taxonomy-pages',
            'The A1 role requires deep technical knowledge combined with client communication and calm under pressure. Experience expectations vary by show. <a href="/roles/audio-engineer">View the Audio Engineer role page on StageCrew</a>.',
          ),
        ],
      },
      {
        heading: 'What an A2 (Audio Technician) Actually Does',
        content: [
          'The A2 is the A1\'s right hand. While the A1 focuses on the mix and system design, the A2 handles the physical infrastructure: running cables, setting up microphone stands, wiring patch panels, managing wireless microphone systems, and handling all backstage audio needs.',
          'On show day, the A2 is typically backstage managing microphones for presenters (swapping batteries, checking RF frequencies, troubleshooting dead packs), while the A1 is at the mix position. The A2 is also responsible for RF coordination — scanning frequencies and making sure wireless microphones don\'t interfere with each other.',
          productClaim(
            'roles.taxonomy-pages',
            'Many A2s describe their job as "making the A1 look good," and that\'s accurate. <a href="/roles/audio-technician">View the Audio Technician role page on StageCrew</a>.',
          ),
        ],
      },
      {
        heading: 'How to Compare A1 and A2 Offers',
        content: [
          'A2 offers can differ by patching, RF, stage, rehearsal, travel, and show-day responsibilities.',
          'A1 offers can differ by system design, advance work, console, input count, rehearsal, broadcast, recording, and client-facing responsibility.',
          'Do not infer a fixed pay gap between the roles. Compare the complete scope and verify current local rates and terms.',
        ],
      },
      {
        heading: 'Skills and Certifications That Matter',
        content: [
          'For A2 roles, employers look for: familiarity with common wireless systems (Shure Axient, Sennheiser Digital 6000), basic soldering ability, Dante networking basics, and a professional attitude. No formal degree is required.',
          'For A1 roles: employers may ask for experience with multiple console platforms, system design, networking, and role-relevant training.',
          'Certifications that help at both levels: OSHA 10-Hour, ETCP for rigging awareness, and CTS from AVIXA for corporate AV work.',
        ],
      },
      {
        heading: 'How to Advance from A2 to A1',
        content: [
          'The path from A2 to A1 requires intentional skill-building and relationship development. Start by learning the console platform your company uses, ask your A1s to let you mix soundchecks and rehearsals, then take small A1 gigs (breakout rooms, small stages, panel discussions) while continuing to A2 on larger shows.',
          'Relationships matter because A1s may recommend A2s and production managers may offer broader responsibilities to people they trust. No credential or contact guarantees advancement.',
          'Build role-relevant skills with tools such as RF scanning, Wireless Workbench, and system-prediction software. Demonstrating those skills can support broader responsibilities but does not guarantee promotion.',
        ],
      },
    ],
    conclusion: [
      'The A1/A2 structure separates mix and system responsibility from stage, patching, microphone, and RF support. Staffing and progression vary by production.',
      productClaim(
        [
          'roles.taxonomy-pages',
          'jobs.active-discovery',
          'accounts.free-registration',
          'workers.profile-preferences',
          'workers.profile-matching',
        ],
        'StageCrew publishes separate A1 and A2 role pages. <a href="/roles/audio-engineer">Review the Audio Engineer role</a>, <a href="/roles/audio-technician">review the Audio Technician role</a>, or <a href="/register?role=worker">create a worker profile</a> for profile-based matching. Current listings and matches are not guaranteed.',
      ),
    ],
  },
  'how-to-start-stagehand-career': {
    intro: [
      'Here\'s something the live events industry doesn\'t advertise well enough: you can start working as a stagehand with zero experience, no degree, and no certifications. Every lighting designer, audio engineer, and technical director in this business started somewhere — and for most of them, that somewhere was pushing road cases and coiling cables.',
      'If you\'re looking for physical work connected to concerts, corporate events, and festivals, stagehand work may be worth researching. Review each call\'s requirements and terms before applying.',
    ],
    sections: [
      {
        heading: 'What Does a Stagehand Actually Do?',
        content: [
          'A stagehand is the backbone of every live event. The job breaks into three phases: load-in, show, and load-out. During load-in, you\'re unloading trucks, building stages, setting up truss, running cables, and placing speakers. During the show, you might be backstage managing set changes or operating a follow spot. During load-out, everything comes down and goes back in the trucks.',
          'The work can involve lifting, long periods on your feet, changing schedules, weather, noise, and venue-specific safety requirements. Verify the physical demands and expected hours for each call.',
          productClaim(
            ['roles.taxonomy-pages', 'jobs.active-discovery'],
            'Stagehand rates vary by location, union agreement, venue, role, schedule, contract, and experience. <a href="/roles/stagehand">Review the Stagehand role page and current listings</a>, then confirm the complete terms directly.',
          ),
        ],
      },
      {
        heading: 'How to Get Your First Gig (With No Experience)',
        content: [
          'Entry requirements vary. Some calls accept new workers, while others require venue experience, safety credentials, lifting ability, tools, or specialized equipment knowledge.',
          productClaim(
            ['accounts.free-registration', 'workers.profile-preferences'],
            'Start by creating a worker profile on <a href="/register?role=worker">StageCrew</a> and recording stagehand as a requested role. Be honest about your experience level — crews expect to train new hands. You can also reach out directly to local production companies and AV rental houses.',
          ),
          'For early calls, arrive before the stated call time, follow safety and crew-lead instructions, ask before handling unfamiliar equipment, and be honest about your experience.',
        ],
      },
      {
        heading: 'Certifications That Give You an Edge',
        content: [
          'You don\'t need certifications to start, but a few affordable ones make you more hirable:',
          '<strong>OSHA safety training:</strong> Some venues or employers ask for specific training. <strong>Forklift certification:</strong> Some calls require a current credential from an accepted provider. Verify the exact course, provider, renewal, and role requirements before paying.',
          '<strong>Rigging Awareness:</strong> A basic rigging safety course shows crew leads you understand overhead hazards. <strong>First Aid/CPR:</strong> Simple, cheap, and occasionally required for outdoor festivals.',
        ],
      },
      {
        heading: 'Career Paths: Where Stagehand Work Can Take You',
        content: [
          'Stagehand work can lead toward audio support, lighting, video, rigging, scenic, carpentry, or production coordination, depending on training and opportunities.',
          'Some specialties require certifications, supervised experience, or venue approval. Research the role and accepted credentials before investing in training.',
          'There is no standard advancement timeline or guaranteed pay outcome. Build experience safely, document the equipment and environments you know, and verify each offer.',
        ],
      },
    ],
    conclusion: [
      'Stagehand listing volume and entry requirements vary by market and season. Review current calls, be candid about your experience, and confirm the rate, schedule, safety requirements, and hiring entity.',
      productClaim(
        [
          'accounts.free-registration',
          'workers.profile-preferences',
          'workers.profile-matching',
          'roles.taxonomy-pages',
          'jobs.active-discovery',
        ],
        'Ready to get started? <a href="/register?role=worker">Create a free StageCrew worker profile</a> for profile-based matching, then <a href="/roles/stagehand">review the Stagehand role page and current listings</a>. Matches and open positions vary by market.',
      ),
    ],
  },
  'ab5-event-staffing-california': {
    intro: [
      'California worker-classification rules are fact-specific and can change. This article is general background, not legal, payroll, or tax advice; consult qualified California counsel about each engagement.',
      'Do not assume a worker is properly classified based only on a contract label, invoice, business entity, or industry custom. Confirm the current legal test and your obligations with a qualified professional.',
    ],
    sections: [
      {
        heading: 'What AB5 Actually Says (The ABC Test)',
        content: [
          'AB5 codified the "ABC test" from the Dynamex decision. Under this test, a worker is presumed to be an employee unless the hiring entity can demonstrate all three conditions: <strong>(A)</strong> The worker is free from control and direction. <strong>(B)</strong> The worker performs work outside the usual course of the hiring entity\'s business. <strong>(C)</strong> The worker is customarily engaged in an independently established trade of the same nature.',
          'For event staffing companies, Prong B is the critical problem. If your business is providing AV technicians for events, then the AV technicians you hire are performing work within the usual course of your business. That alone fails the ABC test, regardless of contract structure.',
          'All three prongs must be satisfied — failing any single one means the worker is legally an employee.',
        ],
      },
      {
        heading: 'Why Most Event Staffing Companies Are Non-Compliant',
        content: [
          'A dispatch model using independent contractors can raise classification questions under California law. The result depends on current law and the facts of the relationship.',
          'Classification can affect payroll, taxes, insurance, workers\' compensation, wage-and-hour obligations, notices, and remedies.',
          'Do not assume outside clients, an LLC, an invoice, or a signed contractor agreement resolves the issue. Ask qualified counsel which test and exceptions apply.',
        ],
      },
      {
        heading: 'Penalties and Enforcement',
        content: [
          'Potential exposure can include civil penalties, taxes, wages, overtime, meal or rest-period claims, insurance issues, and notice or recordkeeping violations.',
          'The amount, lookback period, responsible parties, defenses, and available remedies depend on current law and the facts.',
          'Use current official California guidance and qualified counsel rather than an unsourced online penalty estimate.',
        ],
      },
      {
        heading: 'How to Stay Compliant',
        content: [
          '<strong>Option 1: Hire as W-2 employees.</strong> Payroll, withholding, workers\' compensation, notices, and other obligations vary by engagement. Get current guidance from qualified California counsel and a payroll professional.',
          '<strong>Option 2: Use an appropriate employer-of-record or staffing provider.</strong> Confirm in writing which entity employs the worker and handles payroll, insurance, and required notices. Do not assume a job marketplace is the employer of record.',
          '<strong>Option 3: Review the business relationship.</strong> Qualified counsel can assess whether an exception or a different contracting structure applies; a marketplace cannot make that legal determination for you.',
        ],
      },
    ],
    conclusion: [
      'California classification can create significant obligations and exposure. Get current, engagement-specific advice from qualified California counsel and payroll professionals.',
      'A marketplace listing does not determine worker classification or replace payroll, tax, or legal advice. California rules can change, so consult a qualified professional about each engagement.',
    ],
  },
  'why-av-staffing-agencies-are-failing': {
    intro: [
      'AV staffing still relies on a mix of calls, texts, referrals, job boards, and agencies. That fragmentation can make it difficult for workers and employers to compare current opportunities, terms, and application status.',
      'Marketplaces can improve visibility and workflow, but they do not replace qualification checks, direct communication, contracts, payroll responsibilities, or local compliance.',
    ],
    sections: [
      {
        heading: 'The Workflow Is Fragmented',
        content: [
          'Calls, emails, referrals, and separate job boards can make the same opportunity difficult to track from source through application.',
          'Workers benefit when a listing clearly states the role, date, location, rate, source, and application method. Employers benefit when candidate information is organized for review.',
          'A marketplace can reduce some search friction, but listing volume and candidate response still vary by role, market, date, and source.',
        ],
      },
      {
        heading: 'Payment Terms Need Direct Confirmation',
        content: [
          'Workers should confirm the hiring entity, rate, payment schedule, invoicing requirements, and dispute contact before accepting an engagement.',
          'Employers should put those terms in writing and communicate changes before the call.',
          'Before accepting a job, confirm who processes payroll or contractor payment, the timing, required documents, and the dispute path.',
        ],
      },
      {
        heading: 'Compare the Complete Cost and Service',
        content: [
          'An agency fee may cover sourcing, payroll, insurance, dispatch, replacements, or other services. Ask what is included instead of comparing the worker rate and invoice total in isolation.',
          'A marketplace subscription has a different service model and does not automatically provide those agency functions.',
          'Choose the model that fits the engagement after comparing responsibilities, service, risk, and total cost.',
        ],
      },
      {
        heading: 'Zero Technology in a Tech-Driven World',
        content: [
          'Some staffing workflows still depend on phone calls, text messages, and spreadsheets, while others use purpose-built systems.',
          'The same company that deploys a 4K LED wall with real-time content management is booking the crew for that wall using a text message chain that started with "hey, you available Tuesday?"',
          'Centralized source links, candidate statuses, and saved preferences can make outreach easier to review, but technology alone cannot guarantee staffing.',
        ],
      },
      {
        heading: 'What Replaces the Traditional Model',
        content: [
          'Marketplaces can let workers save preferences and review sourced listings while giving employers a place to post gigs and manage candidate statuses.',
          'Healthcare staffing, warehouse labor, and restaurant shift work have all been transformed by marketplace platforms. AV staffing is following the same trajectory.',
          productClaim(
            [
              'jobs.active-discovery',
              'employers.gig-posting',
              'workers.profile-preferences',
              'workers.profile-matching',
              'applications.status-workflow',
            ],
            'At <a href="/about">StageCrew</a>, we organize sourced listings, employer-posted gigs, saved worker preferences, profile-based matching, and application or candidate status visibility. Workers and employers still confirm terms directly.',
          ),
        ],
      },
    ],
    conclusion: [
      'The traditional AV staffing agency model was built for an era when information was scarce and relationships were the only distribution channel. That era is over.',
      productClaim(
        'accounts.free-registration',
        'To compare the current marketplace workflow yourself, <a href="/register">create a StageCrew account</a>. Signup does not guarantee work, staffing, or payment outcomes.',
      ),
    ],
  },
  'stagehand-jobs-las-vegas-guide': {
    intro: [
      'Las Vegas is one of the best markets in the country for stagehand work because conventions, residencies, arena shows, sports, casino entertainment, and corporate productions all stack on top of each other.',
      'If you are trying to get more Las Vegas stagehand jobs, the goal is simple: be easy to book, be clear about your skills, and get into the channels where production managers already look when they need people fast.',
    ],
    sections: [
      {
        heading: 'Where Las Vegas stagehand work comes from',
        content: [
          'The biggest sources of work are convention load-ins, hotel ballroom AV, casino entertainment, arena shows, trade shows, sports events, and seasonal corporate meetings. That creates demand for general hands, forklift-certified hands, rigging support, LED wall crew, audio support, and lighting techs.',
          'Strong local search pages matter here because buyers do not always search for "event labor marketplace." They search for the role and city: stagehand jobs Las Vegas, Las Vegas event crew, Las Vegas convention labor, or stagehands near the Strip.',
          productClaim(
            'markets.role-city-pages',
            'Start with the local StageCrew pages for <a href="/gigs/las-vegas/stagehand">Las Vegas stagehand gigs</a>, <a href="/hire/stagehands/las-vegas">Las Vegas stagehand hiring</a>, and the <a href="/markets/las-vegas">Las Vegas market hub</a>.',
          ),
        ],
      },
      {
        heading: 'Rates and skills that get attention',
        content: [
          'Rates vary by source, role, venue, schedule, union agreement, experience, and contract. Compare current Las Vegas listings and written offers rather than relying on a generic range.',
          'The profile details that help most are lift experience, OSHA 10, forklift certification, rigging awareness, convention center experience, black wardrobe readiness, and whether you can handle overnight load-outs.',
          'Showing up prepared, following instructions, and staying calm when plans change can support a professional reputation, but it does not guarantee another call.',
        ],
      },
      {
        heading: 'How to get called back',
        content: [
          'After every call, send a short follow-up to the crew lead: "Thanks for having me today. I am available for more stagehand, load-in, and strike calls this month." That small message keeps you top of mind.',
          productClaim(
            'workers.profile-preferences',
            'Keep your StageCrew profile current with the exact roles you want: stagehand, rigger, AV technician, LED technician, or audio technician. Accurate preferences give matching and employer review more specific information; they do not guarantee visibility or work.',
          ),
          'If you can travel from Las Vegas to Inland Empire, Los Angeles, Orange County, or Phoenix for larger calls, say that clearly. Regional availability can fill the dead weeks between local convention spikes.',
        ],
      },
    ],
    conclusion: [
      'Las Vegas rewards stagehands who are reliable, specific, and easy to book. Treat every load-in like a tryout for the next production manager\'s shortlist.',
      productClaim(
        ['workers.profile-preferences', 'markets.role-city-pages'],
        'Create or update your <a href="/register?role=worker">StageCrew worker profile</a>, then review the <a href="/gigs/las-vegas/stagehand">Las Vegas stagehand jobs page</a> for current listings.',
      ),
    ],
  },
  'av-technician-jobs-orlando-guide': {
    intro: [
      'Orlando AV calls can include convention work, hotel AV, theme park production, corporate general sessions, livestreams, and LED-heavy brand activations.',
      'AV technicians can search across breakout rooms, general sessions, projection, audio support, LED builds, video playback, camera, and hybrid event support.',
    ],
    sections: [
      {
        heading: 'Orlando AV work streams to research',
        content: [
          'Convention and hotel AV calls often include breakout-room and general-session work. Breakout rooms need techs who can set, test, troubleshoot, and support presenters; general sessions may need more specialized audio, video, lighting, and comms support.',
          'Theme park and entertainment work can be more specialized, but it teaches polish. If you have experience with show control, scenic environments, guest-facing standards, or attraction support, put that on your profile.',
          productClaim(
            ['markets.role-city-pages', 'vendors.directory'],
            'Use the local pages for <a href="/gigs/orlando/av-technician">Orlando AV technician gigs</a>, <a href="/hire/av-technicians/orlando">hiring AV technicians in Orlando</a>, and <a href="/vendors/city/orlando">Orlando AV vendors</a> to research current worker and buyer paths.',
          ),
        ],
      },
      {
        heading: 'Skills Orlando employers search for',
        content: [
          'The best keywords for an Orlando AV technician profile are breakout rooms, projection, confidence monitors, wireless mics, Dante, QLab, LED wall support, livestream support, Zoom Rooms, Teams Live, camera, SDI, and client-facing corporate AV.',
          'If you can run a room alone, say so. "Breakout room operator" is more searchable and more useful than "AV tech." If you can float between rooms, troubleshoot laptops, and keep speakers calm, that is valuable.',
          'Rates vary by call, scope, skill, venue, employer, and schedule. Verify current Orlando source listings and written offers instead of relying on a generic day-rate range.',
        ],
      },
      {
        heading: 'How to stand out locally',
        content: [
          'Orlando production managers want people who understand long hotel days, fast resets, presenter changes, and last-minute room flips. Mention examples like multi-room conferences, association shows, trade shows, or corporate general sessions.',
          'Keep a short tools list: laptop adapters, console familiarity, comms experience, RF basics, cable tester, laptop, and whether you can drive to OCCC, Disney-area hotels, Universal-area venues, and downtown Orlando.',
          'Keep availability current so employers have better information to review, then confirm each call directly. A current profile does not guarantee selection.',
        ],
      },
    ],
    conclusion: [
      'Orlando includes theme-park, corporate AV, hotel, and convention work, with listing volume that changes by source and season.',
      productClaim(
        ['workers.profile-preferences', 'markets.role-city-pages'],
        'Set up your <a href="/register?role=worker">StageCrew worker profile</a> and browse <a href="/gigs/orlando/av-technician">Orlando AV technician jobs</a> to compare current listings with your skills.',
      ),
    ],
  },
  'hire-a1-audio-engineer-los-angeles': {
    intro: [
      'Hiring an A1 audio engineer in Los Angeles is not the same as hiring general AV help. The A1 is responsible for the sound of the room, the mix, the signal flow, and often the calm of the entire show.',
      'Los Angeles audio work is specialized. A clear show, console, venue, schedule, and scope gives candidates better information to evaluate, but does not guarantee a hire.',
    ],
    sections: [
      {
        heading: 'When you need an A1 instead of an A2',
        content: [
          'You need an A1 when someone must own the mix: keynote audio, bands, panels, playback, livestream audio, broadcast feeds, monitor mixes, or any event where the client will notice immediately if the sound is wrong.',
          'You need an A2 when the show needs patching, microphones, wireless swaps, stage audio, RF support, and cable management under the direction of the A1. On larger shows, you usually need both.',
          productClaim(
            'markets.role-city-pages',
            'If you are not sure, read the <a href="/blog/audio-engineer-vs-audio-technician">A1 vs A2 guide</a>, then review the <a href="/hire/audio-engineers/los-angeles">Hire audio engineers in Los Angeles</a> page.',
          ),
        ],
      },
      {
        heading: 'What to include in the call',
        content: [
          'A strong A1 call includes venue, room size, audience size, show type, console, PA provider, input count, wireless count, show flow, rehearsal time, recording or livestream needs, and whether the engineer is expected to design the system or only operate it.',
          'Los Angeles A1 rates vary with complexity, credits, schedule, advance work, gear, broadcast needs, and client-facing responsibility. Verify current local offers and written terms.',
          'Replace "need audio tomorrow" with a complete call that states the audience and room, console, input and wireless counts, playback, stream feeds, rehearsal, estimated hours, and rate.',
        ],
      },
      {
        heading: 'Questions to ask before confirming',
        content: [
          'Ask which consoles they are comfortable on, whether they have mixed similar events, how they handle RF coordination, what they need from the vendor before load-in, and whether they can advance the show by phone before the call.',
          'For higher-stakes LA shows, ask for specific experience: corporate general sessions, live music, broadcast, awards, studio lots, outdoor festival stages, or hybrid events. Matching the A1 to the environment matters.',
          'Once confirmed, send the call sheet, venue details, parking, contact, input list, run of show, and client audio expectations early enough for the engineer to review.',
        ],
      },
    ],
    conclusion: [
      'A great A1 is part engineer, part show lead, and part problem solver. In Los Angeles, the talent exists, but the details determine whether you get the right person.',
      productClaim(
        'markets.role-city-pages',
        'Start with <a href="/hire/audio-engineers/los-angeles">Los Angeles audio engineer hiring</a> or browse the wider <a href="/markets/los-angeles">Los Angeles event crew market</a>.',
      ),
    ],
  },
  'last-minute-stagehands-inland-empire': {
    intro: [
      'The Inland Empire has its own event calendar, and last-minute crew calls here should not be treated like overflow from Los Angeles. Riverside, Ontario, Palm Springs, San Bernardino, and casino or arena work all need local coverage.',
      'If a stagehand cancels or your load-in changes, this checklist helps organize the need, outreach channels, direct confirmations, and backups. It does not guarantee a fill.',
    ],
    sections: [
      {
        heading: 'First: clarify the real need',
        content: [
          'Write down the exact venue, report time, expected end time, role count, lift requirements, wardrobe, parking, point of contact, pay range, and whether the call is load-in, show call, load-out, or all three.',
          'Separate general hands from specialty roles such as forklift-certified or rigging support. Clear requirements help workers evaluate the call.',
          productClaim(
            ['markets.role-city-pages', 'vendors.directory'],
            'Use the local pages for <a href="/hire/stagehands/inland-empire">hiring Inland Empire stagehands</a>, <a href="/gigs/inland-empire/stagehand">Inland Empire stagehand gigs</a>, and <a href="/vendors/city/inland-empire">Inland Empire vendors</a>.',
          ),
        ],
      },
      {
        heading: 'Next: post once, then work backup channels',
        content: [
          'Post the gig with complete details. Then contact previous crew with a short message using placeholders for the role, venue, call time, estimated hours, scope, rate, and parking.',
          'Avoid vague messages. When workers have to ask for the city, rate, time, or role, you lose the people who were ready to commit quickly.',
          'For same-day or next-day calls, consider a rush premium or reduce friction with guaranteed minimum hours, parking, clear check-in, and prompt payment on the agreed terms.',
        ],
      },
      {
        heading: 'Before call time: confirm like a production manager',
        content: [
          'Send a call sheet to every confirmed worker with venue address, parking instructions, wardrobe, tools, contact, time zone, and a one-line description of the work. Then ask for a direct confirmation reply.',
          'Create a backup plan for mission-critical roles and document who should be contacted if the staffing plan changes.',
          'After the job, mark who showed up on time and who worked well. Your next last-minute call should start with the people who proved themselves.',
        ],
      },
    ],
    conclusion: [
      'Last-minute Inland Empire stagehand calls work best when they are local, specific, and confirmed with a real call sheet.',
      productClaim(
        ['employers.gig-posting', 'markets.role-city-pages'],
        'Post a complete gig through <a href="/post-gig">StageCrew</a>, or start your research from the <a href="/hire/stagehands/inland-empire">Inland Empire stagehand hiring page</a>. Posting does not guarantee responses or staffing.',
      ),
    ],
  },
  'how-to-claim-av-company-page': {
    intro: [
      productClaim(
        'vendors.directory',
        'StageCrew has vendor pages where production buyers can research AV rental companies, staging vendors, audio shops, lighting providers, production companies, and labor providers by market.',
      ),
      productClaim(
        'vendors.claim-flow',
        'If you found your company page, a signed-in employer can confirm control of the website domain already listed, then maintain supported public fields. Domain confirmation does not verify legal ownership or guarantee leads.',
      ),
    ],
    sections: [
      {
        heading: 'Why claiming your page matters',
        content: [
          productClaim(
            ['vendors.claim-flow', 'vendors.directory'],
            'Unmanaged pages can still help buyers discover a company. After website-domain confirmation, the managing employer account can keep supported fields such as the description, specialty, city, contact details, and service area current.',
          ),
          productClaim(
            ['vendors.claim-flow', 'vendors.directory'],
            'A maintained page tells buyers which information the claimant has reviewed; buyers should still verify that the company is active and suitable for the event.',
          ),
          productClaim(
            ['vendors.claim-flow', 'vendors.directory'],
            'Start at <a href="/claim/vendor-listing">Claim your vendor listing</a>, or search directly in the <a href="/vendors?claim=1">vendor claim directory</a>.',
          ),
        ],
      },
      {
        heading: 'How the claim workflow works',
        content: [
          productClaim(
            ['vendors.claim-flow', 'accounts.free-registration'],
            'Search for your company by name, city, or state. Open the listing, then use the claim button. If you are not signed in yet, StageCrew preserves the listing redirect so you come back to the right page after registration.',
          ),
          productClaim(
            'vendors.claim-flow',
            'After the employer account confirms control of the website domain already on the listing, it can update supported business fields such as specialty, service area, description, and public contact information.',
          ),
          productClaim(
            'vendors.claim-flow',
            'If your company has multiple locations or appears under an old name, verify the best match before starting domain confirmation, then update only the supported manager-controlled fields.',
          ),
        ],
      },
      {
        heading: 'What to update after claiming',
        content: [
          'Write the description for the jobs you want, not just the company you are. Good examples: corporate AV in Riverside, union stage labor in Chicago, LED wall rentals in Orlando, or audio production for Nashville live music.',
          'Add the markets you actually serve. Buyers often search by city, so a company serving Riverside, Ontario, Palm Springs, and Los Angeles should make that coverage clear.',
          'Use specific specialties. "Production" is broad. "AV rental, audio, lighting, staging, and union labor" gives buyers and search engines more useful context.',
        ],
      },
    ],
    conclusion: [
      productClaim(
        'vendors.claim-flow',
        'Confirming control of the listed website domain gives the managing employer account a way to keep supported directory details current; it does not verify legal ownership or guarantee buyer leads.',
      ),
      productClaim(
        'vendors.claim-flow',
        'Search for your business on the <a href="/claim/vendor-listing">claim listing page</a>, then update the supported profile fields so buyers have clearer information to review.',
      ),
    ],
  },
  'av-rental-companies-inland-empire-guide': {
    intro: [
      'The Inland Empire has a real AV and production market of its own. Buyers planning events in Riverside, Ontario, Palm Springs, San Bernardino, or casino and arena venues should not have to guess which vendors serve the area.',
      'This guide helps buyers compare AV rental companies before they book gear, technicians, staging, lighting, audio, or full production support.',
    ],
    sections: [
      {
        heading: 'Start with the event type',
        content: [
          'A corporate general session needs a different vendor than a casino concert, university event, outdoor festival, or convention load-in. Before comparing quotes, define whether you need gear rental only, crew only, or a vendor that can own the full show.',
          productClaim(
            ['vendors.directory', 'markets.role-city-pages'],
            'For Inland Empire searches, start with <a href="/vendors/city/inland-empire/av-rental">AV rental companies in the Inland Empire</a>, then compare nearby city pages like <a href="/vendors/city/riverside/av-rental">Riverside AV rental</a>, <a href="/vendors/city/ontario/av-rental">Ontario AV rental</a>, and <a href="/vendors/city/palm-springs/av-rental">Palm Springs AV rental</a>.',
          ),
          productClaim(
            'markets.role-city-pages',
            'If you are hiring crew separately, pair the vendor search with <a href="/hire/av-technicians/inland-empire">Inland Empire AV technicians</a> or <a href="/hire/stagehands/inland-empire">Inland Empire stagehands</a>.',
          ),
        ],
      },
      {
        heading: 'Questions to ask AV rental companies',
        content: [
          'Ask whether the quote includes delivery, setup, strike, labor, insurance, cables, adapters, playback, projection screens, comms, microphones, and a technician during show hours.',
          'Ask what happens if the event changes: room count, run time, presenter count, rain plan, power availability, or late load-out. The best vendors have a change process before show week.',
          'Ask for recent local experience. A vendor that already knows Toyota Arena, Riverside Convention Center, Ontario Convention Center, Palm Springs Convention Center, or casino loading docks can save time on logistics.',
        ],
      },
      {
        heading: 'How to compare quotes fairly',
        content: [
          'Do not compare only the bottom-line number. One quote may include labor and show support while another includes only gear. Normalize the scope before deciding.',
          'Look for clear line items, onsite contact, payment terms, cancellation policy, overtime terms, and replacement plans for critical gear.',
          productClaim(
            ['vendors.claim-flow', 'vendors.directory'],
            'If a listing is unmanaged, the vendor may still be legitimate. A domain-confirmed page identifies information a managing account can maintain, but buyers should still verify every material detail directly.',
          ),
        ],
      },
    ],
    conclusion: [
      'The right Inland Empire AV vendor depends on scope, venue, schedule, and whether you need gear, crew, or full production.',
      productClaim(
        ['vendors.directory', 'vendors.claim-flow'],
        'Use the <a href="/vendors/city/inland-empire/av-rental">Inland Empire AV rental directory</a> to compare vendor information, or <a href="/claim/vendor-listing">claim your company page</a> if your business is listed.',
      ),
    ],
  },
  'stagehand-resume-examples-live-events': {
    intro: [
      'A stagehand resume does not need to look corporate. It needs to answer the questions a crew lead actually has: can you show up, can you work safely, what have you done, and what can I trust you with?',
      productClaim(
        ['workers.public-profiles', 'workers.profile-matching'],
        'Use your resume and StageCrew profile together. The resume supports direct outreach, while the profile gives employers worker-provided details to review and gives matching more saved information. Neither guarantees a gig.',
      ),
    ],
    sections: [
      {
        heading: 'What to put at the top',
        content: [
          'Start with a simple headline: "Stagehand and live event crew, load-in, strike, staging, audio support, Los Angeles." Then list your city, travel radius, phone, email, and availability.',
          'Add a short summary with specifics: "Reliable stagehand with convention, concert, and corporate AV experience. Comfortable with road cases, cable runs, pipe and drape, stage decks, basic audio patch, and overnight strikes."',
          productClaim(
            ['workers.profile-preferences', 'workers.public-profiles'],
            'Link your <a href="/register?role=worker">StageCrew worker profile</a> if you use one, especially if you have enabled signed-in employer visibility for relevant roles, availability details, and work history.',
          ),
        ],
      },
      {
        heading: 'Skills that belong on a stagehand resume',
        content: [
          'List practical skills first: load-in, load-out, truck pack, road cases, staging, risers, truss support, cable runs, pipe and drape, barricade, scenic, audio assist, lighting assist, video assist, forklift, lift operation, and basic rigging awareness.',
          'Then list certifications: OSHA 10, forklift, aerial lift, CPR/first aid, ETCP if applicable, Dante, CTS, or manufacturer training. Do not bury these at the bottom.',
          'Add venue and event types: convention centers, arenas, theaters, hotels, festivals, corporate events, trade shows, church production, touring shows, or broadcast.',
        ],
      },
      {
        heading: 'Example resume bullets',
        content: [
          'Strong bullet: "Supported load-in and strike for a large corporate general session, including stage decks, cable runs, scenic placement, and overnight load-out."',
          'Strong bullet: "Worked convention calls at large hotel and convention venues, handling pipe and drape, cases, breakout room resets, and client-facing room flips."',
          'Weak bullet: "Helped with events." Replace vague lines with role, venue type, task, and scale. Specificity makes you easier to trust.',
        ],
      },
    ],
    conclusion: [
      'A strong stagehand resume is short, specific, and built around trust. Crew leads want to know what you can do safely and whether you will make the day easier.',
      productClaim(
        ['workers.profile-preferences', 'roles.taxonomy-pages'],
        'After updating your resume, update your <a href="/register?role=worker">StageCrew profile</a> and review the <a href="/roles/stagehand">Stagehand role page and current listings</a>.',
      ),
    ],
  },
  'event-crew-call-sheet-template': {
    intro: [
      'A good call sheet prevents no-shows, late arrivals, wrong wardrobe, parking confusion, and the classic "where do I go?" text five minutes before call time.',
      'For event crew, the best call sheet is short enough to read on a phone and detailed enough that a worker can get from their car to the right dock without guessing.',
    ],
    sections: [
      {
        heading: 'The must-have call sheet fields',
        content: [
          'Include event name, venue name, full address, call time, estimated end time, role, department, supervisor, phone number, parking instructions, entrance or loading dock, wardrobe, tools, meal plan, rate, and emergency contact.',
          'Add a one-line job description for each role: "Stagehands: unload truck, build risers, run cable ramps, strike after show." That helps workers self-correct if they arrive and the plan changed.',
          productClaim(
            'employers.gig-posting',
            'If the call is tied to a StageCrew posting, include the job-page link in your direct crew message so recipients can review the same posted details.',
          ),
        ],
      },
      {
        heading: 'Copy-and-send template',
        content: [
          '<strong>Event:</strong> [Event name]. <strong>Venue:</strong> [Venue, full address]. <strong>Call:</strong> [Date, time, time zone]. <strong>Role:</strong> [Role]. <strong>Estimated hours:</strong> [Hours].',
          '<strong>Report to:</strong> [Name, phone]. <strong>Parking/loading:</strong> [Instructions]. <strong>Wardrobe/tools:</strong> [Black clothes, gloves, wrench, PPE]. <strong>Work:</strong> [Load-in, show call, strike, specialty notes].',
          '<strong>Confirm:</strong> Reply "confirmed" so we know you have the details. If anything changes, text [contact] immediately.',
        ],
      },
      {
        heading: 'Details that reduce cancellations',
        content: [
          'Send the call sheet early enough for workers to review, and request direct confirmation before high-risk or last-minute jobs. Clear information can reduce confusion but cannot guarantee attendance.',
          'For large calls, separate departments so audio, lighting, video, staging, and general labor know who they report to and avoid routing every question through one person.',
          'After the gig, save the final call sheet. The next similar event should start from a proven template, not a blank page.',
        ],
      },
    ],
    conclusion: [
      'The best call sheets are boring in the best way: complete, clear, and sent before anyone has to ask.',
      productClaim(
        'employers.gig-posting',
        'Use StageCrew to <a href="/post-gig">post a complete gig</a>, or start with the <a href="/blog/hire-last-minute-event-crew">last-minute event crew guide</a> when the schedule is already tight. Posting does not guarantee staffing.',
      ),
    ],
  },
};

/* ── Component ────────────────────────────────────────────────────── */

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();

  const post = findPublishedBlogPost(ALL_BLOG_POSTS, slug);
  const content = slug ? POST_CONTENT[slug] : undefined;

  useEffect(() => {
    if (post?.tracksAttribution && slug) {
      recordArticleView(slug, location.search);
    }
  }, [location.search, post?.tracksAttribution, slug]);

  if (!post || !content) {
    return (
      <div className="min-h-[60vh] bg-gray-50 px-4 py-16 sm:px-6 lg:px-8">
        <SEOHead
          title="Article not found — StageCrew Blog"
          description="This StageCrew article does not exist or is not published. Browse current StageCrew guides and resources."
          url={slug ? `https://stagecrew.app/blog/${slug}` : 'https://stagecrew.app/blog'}
          canonical={false}
          robots="noindex, follow"
        />
        <section className="mx-auto max-w-3xl rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <p aria-hidden="true" className="text-7xl font-bold text-blue-600">404</p>
          <h1 className="mt-4 text-3xl font-bold text-gray-900">Article not found</h1>
          <p className="mx-auto mt-3 max-w-xl text-gray-600">
            This article does not exist or is not currently published. No replacement content was generated.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link
              to="/blog"
              className="rounded-lg bg-blue-600 px-6 py-3 font-bold text-white transition hover:bg-blue-700"
            >
              Browse StageCrew articles
            </Link>
            <Link
              to="/jobs"
              className="rounded-lg border border-gray-300 bg-white px-6 py-3 font-bold text-gray-700 transition hover:bg-gray-50"
            >
              Browse active jobs
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const handleArticleClickCapture = (event: React.MouseEvent<HTMLElement>) => {
    if (!post.tracksAttribution) return;
    const target = event.target as Element;
    const anchor = target.closest?.('a');
    const href = anchor?.getAttribute('href') || '';
    if (href === '/jobs' || href.startsWith('/jobs?')) {
      markContentDestination('jobs');
    } else if (href === '/register' || href.startsWith('/register?')) {
      markContentDestination('registration');
    }
  };

  const postIndex = BLOG_POSTS.findIndex((p) => p.slug === slug);
  const prevPost = postIndex > 0 ? BLOG_POSTS[postIndex - 1] : null;
  const nextPost = postIndex < BLOG_POSTS.length - 1 ? BLOG_POSTS[postIndex + 1] : null;

  const articleUrl = `https://stagecrew.app/blog/${post.slug}`;
  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `${articleUrl}#article`,
        headline: post.title,
        description: post.excerpt,
        url: articleUrl,
        datePublished: post.date,
        dateModified: post.dateModified || post.date,
        author: { '@type': 'Organization', name: 'StageCrew', url: 'https://stagecrew.app' },
        publisher: { '@type': 'Organization', name: 'StageCrew', url: 'https://stagecrew.app' },
        mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://stagecrew.app' },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://stagecrew.app/blog' },
          { '@type': 'ListItem', position: 3, name: post.title, item: articleUrl },
        ],
      },
    ],
  };

  return (
    <div>
      <SEOHead
        title={`${post.title} — StageCrew Blog`}
        description={post.excerpt}
        url={`https://stagecrew.app/blog/${post.slug}`}
        canonical={`https://stagecrew.app/blog/${post.slug}`}
        type="article"
        keywords={post.keywords}
        jsonLd={articleJsonLd}
      />

      {/* Hero */}
      <section className={`bg-gradient-to-br ${post.heroColor} text-white py-16 md:py-20`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center text-white/80 hover:text-white text-sm mb-6 transition">
            <FaArrowLeft className="mr-2 text-xs" /> Back to Blog
          </Link>
          <span className="inline-block text-xs font-semibold bg-white/20 px-3 py-1 rounded-full mb-4">
            {post.category}
          </span>
          <h1 className="text-3xl md:text-5xl font-bold mb-4">{post.title}</h1>
          <div className="flex items-center gap-4 text-sm text-white/70">
            <span className="flex items-center gap-1">
              <FaCalendarAlt />
              {new Date(post.date).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                timeZone: 'UTC',
              })}
            </span>
            <span className="flex items-center gap-1">
              <FaClock />
              {post.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <article className="py-12 md:py-16" onClickCapture={handleArticleClickCapture}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Intro */}
          <div className="mb-10">
            {content.intro.map((para, i) => (
              renderRichParagraph(para, 'text-lg text-gray-700 mb-4 leading-relaxed', i)
            ))}
          </div>

          {/* Sections */}
          {content.sections.map((section, si) => (
            <section key={si} className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{section.heading}</h2>
              {section.content.map((para, pi) => (
                renderRichParagraph(para, 'text-gray-700 mb-4 leading-relaxed', pi)
              ))}
              {section.table && (
                <div className="my-6 overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full border-collapse text-left text-sm text-gray-700">
                    <caption className="bg-gray-50 px-4 py-3 text-left font-semibold text-gray-900">
                      {section.table.caption}
                    </caption>
                    <thead className="border-y border-gray-200 bg-gray-50">
                      <tr>
                        {section.table.headers.map(header => (
                          <th key={header} scope="col" className="whitespace-nowrap px-4 py-3 font-semibold text-gray-900">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {section.table.rows.map((row, rowIndex) => (
                        <tr key={`${row[0]}-${rowIndex}`}>
                          {row.map((cell, cellIndex) => cellIndex === 0 ? (
                            <th key={cellIndex} scope="row" className="whitespace-nowrap px-4 py-3 font-medium text-gray-900">
                              {cell}
                            </th>
                          ) : (
                            <td key={cellIndex} className="whitespace-nowrap px-4 py-3">{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {section.table.note && (
                    <p className="border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                      {section.table.note}
                    </p>
                  )}
                </div>
              )}
              {section.relatedLinks && (
                <nav aria-label={`Related resources for ${section.heading}`} className="mt-5">
                  <ul className="flex flex-wrap gap-2">
                    {section.relatedLinks.map(link => (
                      <li key={link.href}>
                        <Link
                          to={link.href}
                          className="inline-flex min-h-11 items-center rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 font-medium text-indigo-700 transition hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                        >
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}
            </section>
          ))}

          {/* Conclusion */}
          <div className="border-t border-gray-200 pt-8 mt-10">
            {content.conclusion.map((para, i) => (
              renderRichParagraph(para, 'text-lg text-gray-700 mb-4 leading-relaxed font-medium', i)
            ))}
          </div>

          {/* CTA Box */}
          <div
            className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-8 mt-10 border border-indigo-100"
            data-content-truth="neutral-blog-article-cta"
          >
            <h3 className="text-xl font-bold text-gray-900 mb-2">Explore More Resources</h3>
            <p className="text-gray-600 mb-4">
              Choose the resources that fit your role in live events.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/register?role=worker"
                className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-bold text-center hover:bg-indigo-700 transition"
              >
                Worker Resources
              </Link>
              <Link
                to="/register?role=employer"
                className="bg-white text-indigo-600 px-6 py-3 rounded-lg font-bold text-center border border-indigo-200 hover:bg-indigo-50 transition"
              >
                Employer Resources
              </Link>
            </div>
          </div>

          {/* Prev / Next Navigation */}
          <div className="flex justify-between items-center mt-12 pt-8 border-t border-gray-200">
            {prevPost ? (
              <Link to={`/blog/${prevPost.slug}`} className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 transition font-medium">
                <FaArrowLeft className="text-xs" /> {prevPost.title}
              </Link>
            ) : <div />}
            {nextPost ? (
              <Link to={`/blog/${nextPost.slug}`} className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 transition font-medium text-right">
                {nextPost.title} <FaArrowRight className="text-xs" />
              </Link>
            ) : <div />}
          </div>
        </div>
      </article>
    </div>
  );
};

export default BlogPostPage;
